import { injectable } from 'inversify';

import { PolicyCategoryItem } from '@app/gen/policy';

import { CategoryNode } from '../types';

interface TreeViewOption {
  id: string;
  label: string;
  children: TreeViewOption[];
  hasChildrenToLoad: boolean;
}

@injectable()
export class CategoryMapper {
  processCategoryList(
    nodes: PolicyCategoryItem[],
    expandedIds: Set<string>,
    level = 0,
    parentName?: string,
  ): CategoryNode[] {
    const result: CategoryNode[] = [];

    nodes.forEach((node) => {
      result.push({
        id: node.id,
        name: node.name,
        documentsCount: node.policies_amount ?? 0,
        level,
        hasChildren: node.children?.length > 0,
        canDelete: !node.have_children && !node.policies_amount,
        parentName,
      });

      if (node.children?.length > 0 && expandedIds.has(node.id)) {
        result.push(
          ...this.processCategoryList(
            node.children,
            expandedIds,
            level + 1,
            node.name,
          ),
        );
      }
    });

    return result;
  }

  findNodeById(
    nodes: PolicyCategoryItem[],
    id: string,
  ): PolicyCategoryItem | undefined {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }

      if (node.children?.length) {
        const found = this.findNodeById(node.children, id);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  }

  processTreeViewOptions(nodes: PolicyCategoryItem[]): TreeViewOption[] {
    return nodes.map((node) => ({
      id: node.id,
      label: node.name,
      children: node.children ? this.processTreeViewOptions(node.children) : [],
      hasChildrenToLoad: false,
    }));
  }
}
